import { useRestClient } from '@/contexts/QueryProvider'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { ENDPOINTS, isPlatformAdmin, isSuperUser } from '@/util'
import { OrganisationDetailsForm } from '../Forms/OrganisationDetailsForm'
import { Button } from '@/components/shadcn/button'
import { IconEditOutline } from '@icons/IconEditOutline'
import { useSessionStorage } from '@/hooks'
import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { Suspense } from 'react'
import { Badge } from '@/components/shadcn/badge'
import { Separator } from '@/components/shadcn/separator'
import { IconV2BuildingOutline } from '@icons/IconV2BuildingOutline'

export function OrganisationDetailsBanner() {
  const organisation = useCurrentOrganisation()
  const { name, coBrandingImageKey, longName, description, whitelistedDomain, active, theme } = organisation
  const parsedTheme = theme ? JSON.parse(theme) : null

  return (
    <div className="relative flex shrink-0 flex-row justify-between gap-x-20 gap-y-4 border-b pb-4">
      <div className="flex shrink-0 gap-5">
        {coBrandingImageKey ? (
          <img
            className="size-28 shrink-0 object-contain"
            src={`${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=${coBrandingImageKey}`}
            alt="Co-branding logo"
          />
        ) : (
          <IconV2BuildingOutline className="size-28 shrink-0 fill-current text-primary" />
        )}
        <div className="self-stretch py-2">
          <Separator orientation="vertical" />
        </div>
        <div className="flex flex-col gap-4 py-1">
          <div>
            <div className="flex items-center gap-5">
              <h3 className="text-2xl font-bold">
                {longName} <span className="font-normal">({name})</span>
              </h3>
              <Badge className="h-5 font-bold uppercase" variant={active ? 'success' : 'destructive'}>
                {active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div className="text-sm font-light">{description}</div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">Color theme</span>
            {parsedTheme && Object.keys(parsedTheme).length > 0 ? (
              <div className="flex gap-3 text-sm">
                <div className="flex items-center gap-1">
                  <span>Primary:</span>
                  <div className="size-4 rounded-lg" style={{ backgroundColor: parsedTheme.primary }} />
                </div>
                <div className="flex items-center gap-1">
                  <span>Secondary:</span>
                  <div className="size-4 rounded-lg" style={{ backgroundColor: parsedTheme.secondary }} />
                </div>
                <div className="flex items-center gap-1">
                  <span>Accent:</span>
                  <div className="size-4 rounded-lg" style={{ backgroundColor: parsedTheme.accent }} />
                </div>
              </div>
            ) : (
              <span className="text-sm font-light">PWC Default Theme</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-end px-10">
        <div className="flex flex-col rounded-xl border bg-background px-5 pb-3 pt-2 font-light">
          <span className="text-sm font-medium">Whitelisted domain</span>
          <span>{whitelistedDomain || 'None'}</span>
        </div>
      </div>

      <QueryErrorBoundary errorMessage="Error fetching Admins">
        <Suspense fallback={null}>
          <EditOrg />
        </Suspense>
      </QueryErrorBoundary>
    </div>
  )
}

function EditOrg() {
  const organisation = useCurrentOrganisation()
  const { getSubscriptionsApiListOrganisationPlatformAdmins } = useRestClient()
  const { id } = organisation
  const { data: currentAdmins } = getSubscriptionsApiListOrganisationPlatformAdmins.useSuspenseQuery({
    query: { organisationId: id },
  })

  const [userRole] = useSessionStorage('roles', 1)
  const [sub] = useSessionStorage('sub', '')
  const canEditOrg =
    isSuperUser(userRole) || (isPlatformAdmin(userRole) && currentAdmins?.some((admin) => admin.id.toString() == sub))

  return (
    <OrganisationDetailsForm
      sheetTitle="Edit organisation details"
      sheetTriggerTooltip="Edit organisation details"
      sheetDescription="Edit the details of the organisation."
      sheetTrigger={
        <Button disabled={!canEditOrg} variant="ghost" size="icon" className="absolute right-0 top-0">
          <IconEditOutline />
        </Button>
      }
      selectedOrg={organisation}
    />
  )
}
